/**
 * pushNotifications — integración OneSignal Web SDK v16 (PUSH Fase 1A).
 *
 * Principios:
 *  - Carga LAZY: el SDK solo se inyecta si el backend dice enabled
 *    (GET /push/config). Sin config → la app funciona exactamente igual.
 *  - Identidad: OneSignal.login(<user_id interno>) — jamás email/nombre/
 *    rol. Idempotente por sesión de página; logout() al cerrar sesión.
 *  - Service worker DEDICADO en /push/onesignal/ (scope propio) para no
 *    chocar con un futuro service worker PWA en la raíz.
 *  - El permiso del navegador SOLO se pide tras un click explícito del
 *    usuario (requestPushPermission) — nunca al entrar.
 *  - Todo es best-effort: ningún fallo de push debe romper la app
 *    (cada export atrapa sus propios errores donde procede el caller).
 */
import { getPushConfig } from "@/lib/pushApi";

const SDK_URL = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
// CONTRATO DEL SERVICE WORKER (no mover a raíz):
//   archivo servido en   /push/onesignal/OneSignalSDKWorker.js
//   scope de registro    /push/onesignal/
// Integración OneSignal "Custom Code": la FUENTE DE VERDAD del path/
// scope es OneSignal.init() (serviceWorkerPath + serviceWorkerParam,
// abajo) — el Dashboard NO necesita configuración manual de service
// workers. Si estos valores cambian, deben cambiar a la vez el archivo
// físico en frontend/public/push/onesignal/ y estas dos constantes.
const SW_PATH = "push/onesignal/OneSignalSDKWorker.js";
const SW_SCOPE = "/push/onesignal/";

let sdkLoadPromise = null;
// initedAppId se asigna SOLO cuando OneSignal.init() ha terminado con
// éxito; initPromise absorbe llamadas concurrentes y se limpia si init
// falla, para que el siguiente intento de la misma page session pueda
// reintentar (un fallo transitorio de red no congela el push). Sin
// harness de tests JS: validado por Babel/webpack + revisión.
let initPromise = null;
let initedAppId = null;
let lastLoginId = null;
let configPromise = null;

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "Notification" in window &&
    "PushManager" in window
  );
}

export function isIosDevice() {
  const ua = navigator.userAgent || "";
  // iPadOS moderno se presenta como MacIntel con touch.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
}

export function isStandalonePwa() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator.standalone === true
  );
}

/** iOS solo permite Web Push desde una PWA añadida a la pantalla de
 * inicio (16.4+): en ese caso NO se debe intentar el prompt. */
export function needsIosHomeScreenInstall() {
  return isIosDevice() && !isStandalonePwa();
}

function loadSdk() {
  if (sdkLoadPromise) return sdkLoadPromise;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  sdkLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => {
      sdkLoadPromise = null; // permite reintentar en el siguiente uso
      reject(new Error("onesignal sdk load failed"));
    };
    document.head.appendChild(s);
  });
  return sdkLoadPromise;
}

function withOneSignal(fn) {
  return new Promise((resolve, reject) => {
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        resolve(await fn(OneSignal));
      } catch (e) {
        reject(e);
      }
    });
  });
}

async function initPush(appId) {
  if (!isPushSupported() || !appId) return false;
  if (initedAppId) return true; // ya inicializado CON ÉXITO en esta página
  if (!initPromise) {
    initPromise = (async () => {
      await loadSdk();
      await withOneSignal((OneSignal) =>
        OneSignal.init({
          appId,
          serviceWorkerPath: SW_PATH,
          serviceWorkerParam: { scope: SW_SCOPE },
        }),
      );
      initedAppId = appId; // SOLO tras un init correcto
      return true;
    })().catch((e) => {
      initPromise = null; // fallo → el siguiente intento reintenta
      throw e;
    });
  }
  return initPromise;
}

async function fetchConfigOnce() {
  if (!configPromise) {
    configPromise = getPushConfig().catch(() => {
      configPromise = null; // un fallo transitorio no congela el estado
      return { enabled: false };
    });
  }
  return configPromise;
}

/**
 * Tras autenticarse (o restaurar sesión): si push está habilitado en el
 * backend, inicializa el SDK y ata la identidad del dispositivo a
 * nuestro user_id (external_id). Idempotente; jamás lanza; jamás pide
 * permiso por sí misma.
 */
export async function ensurePushIdentity(userId) {
  try {
    if (!isPushSupported() || !userId) return;
    const cfg = await fetchConfigOnce();
    if (!cfg?.enabled || !cfg.app_id) return;
    const ok = await initPush(cfg.app_id); // login NUNCA sin init confirmado
    if (!ok) return;
    const uid = String(userId);
    if (lastLoginId === uid) return; // login idempotente
    await withOneSignal((OneSignal) => OneSignal.login(uid));
    lastLoginId = uid; // SOLO tras un login correcto (reintentable si falla)
  } catch {
    /* push jamás rompe la app */
  }
}

/** Al cerrar sesión: desliga el dispositivo de la identidad. */
export async function pushLogout() {
  try {
    if (!initedAppId || lastLoginId === null) return;
    lastLoginId = null;
    await withOneSignal((OneSignal) => OneSignal.logout());
  } catch {
    /* best-effort */
  }
}

/** Estado actual para la UI de Settings. */
export async function getPushState() {
  if (!isPushSupported()) return { supported: false };
  const cfg = await fetchConfigOnce();
  if (!cfg?.enabled || !cfg.app_id) {
    return { supported: true, configured: false, accountEnabled: cfg?.account_enabled !== false };
  }
  const permission = typeof Notification !== "undefined" ? Notification.permission : "default";
  let optedIn = false;
  if (initedAppId) {
    try {
      optedIn = await withOneSignal(
        (OneSignal) => OneSignal.User.PushSubscription.optedIn === true,
      );
    } catch {
      optedIn = false;
    }
  }
  return {
    supported: true,
    configured: true,
    accountEnabled: cfg.account_enabled !== false,
    permission, // "default" | "granted" | "denied"
    optedIn,
    iosNeedsInstall: needsIosHomeScreenInstall(),
  };
}

/**
 * SOLO tras click explícito del usuario: inicializa si hace falta y
 * dispara el prompt nativo. En iOS sin PWA instalada no intenta nada
 * (el caller muestra las instrucciones de pantalla de inicio).
 */
export async function requestPushPermission(userId) {
  if (!isPushSupported() || needsIosHomeScreenInstall()) return false;
  const cfg = await fetchConfigOnce();
  if (!cfg?.enabled || !cfg.app_id) return false;
  await initPush(cfg.app_id);
  if (userId) await ensurePushIdentity(userId);
  const granted = await withOneSignal((OneSignal) =>
    OneSignal.Notifications.requestPermission(),
  );
  if (granted) {
    // Asegura la suscripción del dispositivo tras conceder permiso.
    await withOneSignal((OneSignal) => OneSignal.User.PushSubscription.optIn());
  }
  return granted === true;
}

/** Desactivar/reactivar ESTE dispositivo (la cuenta se gestiona aparte
 * vía POST /push/preference). */
export async function setDeviceOptedOut(optedOut) {
  if (!initedAppId) return;
  await withOneSignal((OneSignal) =>
    optedOut
      ? OneSignal.User.PushSubscription.optOut()
      : OneSignal.User.PushSubscription.optIn(),
  );
}
